import * as React from 'react';

import { Checkbox, Col, Row, Select } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideSlidersHorizontal } from 'lucide-react';

import { useFacilitiesQuery } from './_graphql/useFacilitiesQuery';

import { FormFooter } from '~/modules/settings/_components/_shared/FormFooter';
import { FormSection } from '~/modules/settings/_components/_shared/FormSection';
import { SettingsContent } from '~/modules/settings/_components/_shared/SettingsContent';
import {
  $updateUserPreferencesForm,
  $userPreferencesForm,
} from '~/modules/settings/_state/userForm.state';

/**
 * Checkbox item with label and optional description
 */
type CheckboxItemProps = {
  label: string;
  description?: string;
  isSelected: boolean;
  onChange: (isSelected: boolean) => void;
};

const CheckboxItem = ({
  label,
  description,
  isSelected,
  onChange,
}: CheckboxItemProps) => (
  <Row gap2 className="@CheckboxItem items-start">
    <div className="pt-0.5">
      <Checkbox
        isSelected={isSelected}
        onChange={onChange}
        aria-label={label}
      />
    </div>
    <Col className="gap-0.5">
      <span className="text-sm">{label}</span>
      {description && (
        <span className="text-xs text-neutral/75">{description}</span>
      )}
    </Col>
  </Row>
);

export const UserPreferencesTab = () => {
  const toast = useToast();
  const form = useAtomValue($userPreferencesForm);
  const updateForm = useSetAtom($updateUserPreferencesForm);

  const [isSaving, setIsSaving] = React.useState(false);

  // Fetch facilities for the Primary Facility dropdown
  const { data: { facilities = [] } = {} } = useFacilitiesQuery({
    limit: 1000,
    offset: 0,
  });

  // Build facility options for select
  const facilityOptions = React.useMemo(
    () =>
      facilities.map((f) => ({
        key: f.id,
        label: f.name,
      })),
    [facilities],
  );

  // Mock scale options - in production this would come from an API
  const scaleOptions = React.useMemo(
    () => [
      { key: 'scale-1', label: 'Scale 1' },
      { key: 'scale-2', label: 'Scale 2' },
      { key: 'scale-3', label: 'Scale 3' },
    ],
    [],
  );

  // Mock cash drawer options - in production this would come from an API
  const cashDrawerOptions = React.useMemo(
    () => [
      { key: 'drawer-1', label: 'Cash Drawer 1' },
      { key: 'drawer-2', label: 'Cash Drawer 2' },
    ],
    [],
  );

  const handleDiscard = () => {
    updateForm({
      primaryFacilityId: null,
      primaryScaleId: null,
      defaultCashDrawerId: null,
      enableDefaultPrinting: true,
      skipScalePayment: true,
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // TODO: Implement actual save mutation
      await new Promise((resolve) => setTimeout(resolve, 500));
      toast.success('Preferences updated successfully');
    } catch (error) {
      console.error('Failed to update preferences:', error);
      toast.error('Failed to update preferences');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Col className="@UserPreferencesTab h-full">
      <Col gap6 className="h-0 grow overflow-y-auto">
        <SettingsContent size="medium" className="gap-6 p-5">
          {/* General Preferences Section */}
          <FormSection
            icon={LucideSlidersHorizontal}
            title="General Preferences"
          >
            <Col gap4>
              {/* 3-column select row */}
              <div className="grid grid-cols-3 gap-4">
                <Select
                  label="Primary Facility"
                  value={form.primaryFacilityId ?? ''}
                  onChange={(key) =>
                    updateForm({ primaryFacilityId: key ? String(key) : null })
                  }
                  options={facilityOptions}
                  placeholder="Select Facility..."
                />
                <Select
                  label="Primary Scale"
                  value={form.primaryScaleId ?? ''}
                  onChange={(key) =>
                    updateForm({ primaryScaleId: key ? String(key) : null })
                  }
                  options={scaleOptions}
                  placeholder="Select Scale..."
                />
                <Select
                  label="Default Cash Drawer"
                  value={form.defaultCashDrawerId ?? ''}
                  onChange={(key) =>
                    updateForm({
                      defaultCashDrawerId: key ? String(key) : null,
                    })
                  }
                  options={cashDrawerOptions}
                  placeholder="Select Cash Drawer..."
                />
              </div>

              {/* Other checkboxes section */}
              <Col gap1>
                <span className="text-sm font-semibold">Other</span>
                <div className="rounded-md border border-neutral/20 px-3 py-3.5">
                  <Col className="gap-3.5">
                    <CheckboxItem
                      label="Enable Default Printing"
                      description="Remembers printer choices for different document types"
                      isSelected={form.enableDefaultPrinting}
                      onChange={(isSelected) =>
                        updateForm({ enableDefaultPrinting: isSelected })
                      }
                    />

                    {/* Divider */}
                    <div className="border-t border-neutral/15" />

                    <CheckboxItem
                      label="Skip Scale Payment"
                      isSelected={form.skipScalePayment}
                      onChange={(isSelected) =>
                        updateForm({ skipScalePayment: isSelected })
                      }
                    />
                  </Col>
                </div>
              </Col>
            </Col>
          </FormSection>
        </SettingsContent>
      </Col>

      <SettingsContent size="medium" className="border-t border-neutral/15">
        <FormFooter
          onDiscard={handleDiscard}
          onSave={handleSave}
          isDiscardDisabled={isSaving}
          isSaveDisabled={isSaving}
          className="border-t-0 p-5"
        />
      </SettingsContent>
    </Col>
  );
};
